/**
 * @fileoverview Provider Search Inline - Treatment step provider search.
 *
 * Three-step inline flow aligned with AddProviderModalV2:
 * 1. Search step: 3-source search (resolve + review-found + Google Places)
 *    - Manual entry only available after search completes
 * 2. Draft step: Structured address form + hospital/non-hospital type selection
 * 3. Conflict step: 409 dedup conflict resolution (Use Existing / Create Anyway)
 *
 * Uses shared hooks and utilities:
 * - useProviderResolveSearch - 3-source provider search with broadening fallback
 * - providerDraft utilities - shared types and helpers
 */
import { AlertTriangle, ArrowLeft, Loader2, MapPin, Plus, Search, X } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';

import { ProviderTypeSelector } from '@/components/provider-directory/ProviderTypeSelector';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useProviderResolveSearch } from '@/hooks/useProviderResolveSearch';
import { toTitleCase } from '@/lib/strings';
import {
  enhancedApi as providerDirectoryV2GooglePlacesApi,
  type GooglePlacesAutocompletePredictionV2,
} from '@/services/api/generated/providerDirectoryV2GooglePlacesService';
import {
  type ProviderDedupCandidate,
  type ProviderDirectoryCreateConflictResponseV2,
  useProviderDirectoryV2CreateServiceMutation,
} from '@/services/api/generated/providerDirectoryV2IdentityService';
import {
  medicalProvidersApiClient,
  useUpdateMedicalProviderMutation,
} from '@/services/api/treatmentService';
import {
  ProviderTreatmentStatus,
  TreatmentCareLevel,
} from '@/services/types/treatment-types';
import {
  compactInternalLocation,
  compactLocation,
} from '@/utils/addressFormatting';
import {
  composeAddress,
  EMPTY_DRAFT,
  type ProviderDraft,
  type ResolveProviderMatch,
  toProviderTypeHint,
} from '@/utils/providerDraft';
import { validatePhoneNumber } from '@/utils/validation';

// Re-export ProviderSelection from the canonical location
export type { ProviderSelection } from '@/services/types/provider-directory-v2-types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ProviderSearchInlineProps {
  matterId: string;
  onProviderAdded?: () => void;
  centerLat?: number;
  centerLng?: number;
}

type InlineStep = 'search' | 'draft' | 'conflict';

/** Type guard for RTK Query 409 conflict errors. */
function isConflictError(
  error: unknown,
): error is { status: 409; data: ProviderDirectoryCreateConflictResponseV2 } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    (error as { status: number }).status === 409 &&
    'data' in error &&
    typeof (error as { data: unknown }).data === 'object' &&
    (error as { data: { candidates?: unknown } }).data !== null &&
    Array.isArray(
      (error as { data: { candidates: unknown[] } }).data.candidates,
    )
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ProviderSearchInline: React.FC<ProviderSearchInlineProps> = ({
  matterId,
  onProviderAdded,
  centerLat,
  centerLng,
}) => {
  const dispatch = useDispatch();

  // Wizard step
  const [step, setStep] = useState<InlineStep>('search');

  // 3-source search
  const search = useProviderResolveSearch({ matterId, centerLat, centerLng });

  // Show-all toggles
  const [showAllInternal, setShowAllInternal] = useState(false);
  const [showAllGoogle, setShowAllGoogle] = useState(false);

  // Draft form
  const [draft, setDraft] = useState<ProviderDraft>(EMPTY_DRAFT);
  const [providerType, setProviderType] = useState<
    'hospital' | 'clinic' | null
  >(null);
  const [treatmentStatus, setTreatmentStatus] = useState<ProviderTreatmentStatus | null>(null);
  const [careLevel, setCareLevel] = useState<TreatmentCareLevel | null>(null);

  // Conflict resolution state (409 candidates)
  const [conflictCandidates, setConflictCandidates] = useState<ProviderDedupCandidate[]>([]);
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
  const [createAnywayReason, setCreateAnywayReason] = useState('');
  const [showCreateAnywayForm, setShowCreateAnywayForm] = useState(false);

  // Phone validation
  const [phoneError, setPhoneError] = useState<string | null>(null);

  // Mutations
  const [createMatterProvider, { isLoading: isCreatingProvider }] =
    useProviderDirectoryV2CreateServiceMutation();
  const [updateMedicalProvider] = useUpdateMedicalProviderMutation();
  const [googleDetails, { isFetching: isFetchingDetails }] =
    providerDirectoryV2GooglePlacesApi.endpoints.providerDirectoryV2GooglePlacesDetails.useLazyQuery();

  // Session token ref (avoid stale closure in useCallback)
  const sessionTokenRef = useRef(search.googleSessionToken);
  useEffect(() => {
    sessionTokenRef.current = search.googleSessionToken;
  }, [search.googleSessionToken]);

  // ---------------------------------------------------------------------------
  // Visible items (capped at 6)
  // ---------------------------------------------------------------------------

  const internalVisible = useMemo(
    () =>
      showAllInternal
        ? search.resolveMatches
        : search.resolveMatches.slice(0, 6),
    [search.resolveMatches, showAllInternal],
  );
  const googleVisible = useMemo(
    () =>
      showAllGoogle ? search.googleResults : search.googleResults.slice(0, 6),
    [search.googleResults, showAllGoogle],
  );

  // ---------------------------------------------------------------------------
  // Reset helpers
  // ---------------------------------------------------------------------------

  const resetToSearch = useCallback(() => {
    setStep('search');
    setDraft(EMPTY_DRAFT);
    setProviderType(null);
    setTreatmentStatus(null);
    setCareLevel(null);
    setConflictCandidates([]);
    setSelectedCandidateId(null);
    setCreateAnywayReason('');
    setShowCreateAnywayForm(false);
    setPhoneError(null);
    search.clear();
    setShowAllInternal(false);
    setShowAllGoogle(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.clear]);

  const handleBack = useCallback(() => {
    if (step === 'conflict') {
      setStep('draft');
      setConflictCandidates([]);
      setSelectedCandidateId(null);
      setCreateAnywayReason('');
      setShowCreateAnywayForm(false);
    } else {
      setStep('search');
    }
  }, [step]);

  // ---------------------------------------------------------------------------
  // Search step handlers
  // ---------------------------------------------------------------------------

  const handleInternalPick = useCallback((match: ResolveProviderMatch) => {
    setDraft({
      source: 'internal',
      baseProviderServiceId: match.provider_service_id,
      googlePlaceId: null,
      name: match.name,
      formattedAddress: match.formatted_address ?? '',
      addressLine1: match.address_line1 ?? '',
      addressLine2: match.address_line2 ?? '',
      city: match.city ?? '',
      state: match.state ?? '',
      postalCode: match.postal_code ?? '',
      country: match.country ?? 'US',
      latitude: null,
      longitude: null,
      phone: match.phone ?? '',
      fax: '',
      website: '',
    });
    setProviderType(null);
    setTreatmentStatus(null);
    setCareLevel(null);
    setStep('draft');
  }, []);

  const handleGooglePick = useCallback(
    async (prediction: GooglePlacesAutocompletePredictionV2) => {
      try {
        const details = await googleDetails({
          placeId: prediction.place_id,
          sessionToken: sessionTokenRef.current,
        }).unwrap();

        search.rotateGoogleSessionToken();

        setDraft({
          source: 'google',
          baseProviderServiceId: null,
          googlePlaceId: details.place_id,
          name:
            details.name ?? prediction.main_text ?? prediction.description,
          formattedAddress: details.formatted_address ?? '',
          addressLine1: details.address_line1 ?? '',
          addressLine2: details.address_line2 ?? '',
          city: details.city ?? '',
          state: details.state ?? '',
          postalCode: details.postal_code ?? '',
          country: details.country ?? 'US',
          latitude: details.latitude ?? null,
          longitude: details.longitude ?? null,
          phone: details.phone_number ?? '',
          fax: '',
          website: details.website ?? '',
        });
        setProviderType(null);
        setTreatmentStatus(null);
        setCareLevel(null);
        setStep('draft');
      } catch {
        toast.error('Unable to load Google place details');
      }
    },
    [googleDetails, search],
  );

  const handleGoToCreateNew = useCallback(() => {
    setDraft({ ...EMPTY_DRAFT, name: search.query });
    setProviderType(null);
    setTreatmentStatus(null);
    setCareLevel(null);
    setStep('draft');
  }, [search.query]);

  // ---------------------------------------------------------------------------
  // Draft step handlers
  // ---------------------------------------------------------------------------

  /** Derive the API source value from the draft source. */
  const apiSource = useMemo((): 'internal_existing' | 'google_place' | 'manual' => {
    if (draft.source === 'internal') return 'internal_existing';
    if (draft.source === 'google') return 'google_place';
    return 'manual';
  }, [draft.source]);

  /** Build the address payload shared by create and conflict-resolution calls. */
  const buildAddressPayload = useCallback(
    () => ({
      formatted_address: composeAddress(draft) || null,
      address_line1: draft.addressLine1.trim() || null,
      address_line2: draft.addressLine2.trim() || null,
      city: draft.city.trim() || null,
      state: draft.state.trim() || null,
      postal_code: draft.postalCode.trim() || null,
      country: draft.country.trim() || 'US',
      latitude: draft.latitude,
      longitude: draft.longitude,
      google_place_id: draft.googlePlaceId,
    }),
    [draft],
  );

  const handleCreateOrReuse = useCallback(async () => {
    if (!draft.name.trim()) {
      toast.error('Enter a provider name');
      return;
    }
    if (!providerType) {
      toast.error('Select provider type');
      return;
    }
    if (!treatmentStatus) {
      toast.error('Select treatment status');
      return;
    }
    if (!careLevel) {
      toast.error('Select treatment type');
      return;
    }
    const trimmedPhone = draft.phone.trim();
    if (trimmedPhone) {
      const phoneResult = validatePhoneNumber(trimmedPhone);
      if (!phoneResult.isValid) {
        setPhoneError(phoneResult.errorMessage ?? 'Invalid phone number');
        return;
      }
    }

    const addressPayload = buildAddressPayload();

    try {
      const result = await createMatterProvider({
        providerDirectoryCreateServiceRequestV2: {
          matter_id: matterId,
          source: apiSource,
          base_provider_service_id: draft.baseProviderServiceId ?? null,
          service_name: draft.name.trim(),
          service_type: 'GENERAL',
          provider_type_hint: toProviderTypeHint(providerType),
          address: addressPayload,
          phone: draft.phone.trim() || null,
          fax: draft.fax.trim() || null,
          website: draft.website.trim() || null,
        },
      }).unwrap();

      dispatch(
        medicalProvidersApiClient.util.invalidateTags([
          { type: 'MedicalProviders', id: matterId },
        ]),
      );

      const reused = result.reused_existing ?? false;

      // Patch medical provider with treatment status and care level
      if (result.medical_provider_id && (treatmentStatus || careLevel)) {
        try {
          await updateMedicalProvider({
            matterId,
            providerId: result.medical_provider_id,
            provider: {
              ...(treatmentStatus && { treatment_status: treatmentStatus }),
              ...(careLevel !== null && { care_level: careLevel }),
            },
          }).unwrap();
        } catch {
          toast.error('Provider created but failed to set treatment details');
        }
      }

      toast.success(
        reused
          ? `Linked to existing provider: ${draft.name.trim()}`
          : `Added ${draft.name.trim()}`,
      );
      resetToSearch();
      onProviderAdded?.();
    } catch (err) {
      if (isConflictError(err)) {
        setConflictCandidates(err.data.candidates);
        setSelectedCandidateId(err.data.candidates[0]?.provider_service_id ?? null);
        setCreateAnywayReason('');
        setShowCreateAnywayForm(false);
        setStep('conflict');
      } else {
        toast.error('Failed to create provider');
      }
    }
  }, [
    draft,
    providerType,
    treatmentStatus,
    careLevel,
    buildAddressPayload,
    apiSource,
    createMatterProvider,
    updateMedicalProvider,
    matterId,
    dispatch,
    resetToSearch,
    onProviderAdded,
  ]);

  // ---------------------------------------------------------------------------
  // Conflict resolution handlers
  // ---------------------------------------------------------------------------

  /** User selects one of the duplicate candidates ("Use Existing"). */
  const handleConflictUseExisting = useCallback(async () => {
    if (!selectedCandidateId) {
      toast.error('Select a provider to use');
      return;
    }

    const addressPayload = buildAddressPayload();

    try {
      const useExistingResult = await createMatterProvider({
        providerDirectoryCreateServiceRequestV2: {
          matter_id: matterId,
          source: apiSource,
          base_provider_service_id: draft.baseProviderServiceId ?? null,
          service_name: draft.name.trim(),
          service_type: 'GENERAL',
          provider_type_hint: providerType ? toProviderTypeHint(providerType) : undefined,
          resolution_mode: 'USE_EXISTING',
          selected_candidate_provider_service_id: selectedCandidateId,
          address: addressPayload,
          phone: draft.phone.trim() || null,
          fax: draft.fax.trim() || null,
          website: draft.website.trim() || null,
        },
      }).unwrap();

      dispatch(
        medicalProvidersApiClient.util.invalidateTags([
          { type: 'MedicalProviders', id: matterId },
        ]),
      );

      // Patch treatment details
      if (useExistingResult.medical_provider_id && (treatmentStatus || careLevel)) {
        try {
          await updateMedicalProvider({
            matterId,
            providerId: useExistingResult.medical_provider_id,
            provider: {
              ...(treatmentStatus && { treatment_status: treatmentStatus }),
              ...(careLevel !== null && { care_level: careLevel }),
            },
          }).unwrap();
        } catch {
          toast.error('Provider linked but failed to set treatment details');
        }
      }

      toast.success(`Linked to existing provider: ${draft.name.trim()}`);
      resetToSearch();
      onProviderAdded?.();
    } catch {
      toast.error('Failed to link to existing provider');
    }
  }, [
    selectedCandidateId,
    treatmentStatus,
    careLevel,
    buildAddressPayload,
    apiSource,
    draft,
    providerType,
    createMatterProvider,
    updateMedicalProvider,
    matterId,
    dispatch,
    resetToSearch,
    onProviderAdded,
  ]);

  /** User explicitly creates a new provider despite duplicates ("Create Anyway"). */
  const handleConflictCreateAnyway = useCallback(async () => {
    if (!createAnywayReason.trim()) {
      toast.error('Please provide a reason for creating a new provider');
      return;
    }

    const addressPayload = buildAddressPayload();

    try {
      const createAnywayResult = await createMatterProvider({
        providerDirectoryCreateServiceRequestV2: {
          matter_id: matterId,
          source: apiSource,
          base_provider_service_id: draft.baseProviderServiceId ?? null,
          service_name: draft.name.trim(),
          service_type: 'GENERAL',
          provider_type_hint: providerType ? toProviderTypeHint(providerType) : undefined,
          resolution_mode: 'CREATE_ANYWAY',
          create_anyway_reason: createAnywayReason.trim(),
          address: addressPayload,
          phone: draft.phone.trim() || null,
          fax: draft.fax.trim() || null,
          website: draft.website.trim() || null,
        },
      }).unwrap();

      dispatch(
        medicalProvidersApiClient.util.invalidateTags([
          { type: 'MedicalProviders', id: matterId },
        ]),
      );

      // Patch treatment details
      if (createAnywayResult.medical_provider_id && (treatmentStatus || careLevel)) {
        try {
          await updateMedicalProvider({
            matterId,
            providerId: createAnywayResult.medical_provider_id,
            provider: {
              ...(treatmentStatus && { treatment_status: treatmentStatus }),
              ...(careLevel !== null && { care_level: careLevel }),
            },
          }).unwrap();
        } catch {
          toast.error('Provider created but failed to set treatment details');
        }
      }

      toast.success(`Created new provider: ${draft.name.trim()}`);
      resetToSearch();
      onProviderAdded?.();
    } catch {
      toast.error('Failed to create provider');
    }
  }, [
    createAnywayReason,
    treatmentStatus,
    careLevel,
    buildAddressPayload,
    apiSource,
    draft,
    providerType,
    createMatterProvider,
    updateMedicalProvider,
    matterId,
    dispatch,
    resetToSearch,
    onProviderAdded,
  ]);

  /** Phone blur handler for inline validation. */
  const handlePhoneBlur = useCallback(() => {
    const trimmed = draft.phone.trim();
    if (!trimmed) {
      setPhoneError(null);
      return;
    }
    const result = validatePhoneNumber(trimmed);
    setPhoneError(result.isValid ? null : (result.errorMessage ?? 'Invalid phone number'));
  }, [draft.phone]);

  // ---------------------------------------------------------------------------
  // Derived
  // ---------------------------------------------------------------------------

  const hasSearchQuery = search.query.trim().length >= 2;
  const totalResults =
    search.resolveMatches.length + search.googleResults.length;

  const draftStepTitle =
    draft.source === 'manual' ? 'Create New Provider' : 'Confirm Provider';

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Card>
      <CardContent className="p-2 space-y-2">
        {/* ----- Step 1: Search ----- */}
        {step === 'search' && (
          <>
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400" />
              <Input
                type="text"
                value={search.query}
                onChange={(e) => search.setQuery(e.target.value)}
                placeholder="Search for provider..."
                className="pl-8 h-8 text-xs"
              />
              {search.query && (
                <button
                  type="button"
                  onClick={search.clear}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {/* Search Results */}
            {hasSearchQuery && (
              <>
                <div className="border border-zinc-200 rounded-md max-h-64 overflow-auto">
                  {/* Internal results */}
                  <SectionHeader
                    label="Internal results"
                    count={search.resolveMatches.length}
                    total={search.resolveMatches.length}
                    showAll={showAllInternal}
                    onToggle={() => setShowAllInternal((v) => !v)}
                  />
                  {search.resolveMatches.length === 0 ? (
                    <div className="px-2 py-2 text-xs text-zinc-500 border-b border-zinc-100">
                      {search.isSearching
                        ? 'Searching...'
                        : 'No internal matches'}
                    </div>
                  ) : (
                    internalVisible.map((match) => (
                      <button
                        key={match.provider_service_id}
                        type="button"
                        onClick={() => handleInternalPick(match)}
                        className="w-full text-left px-2 py-1.5 hover:bg-zinc-50 border-b border-zinc-100"
                      >
                        <div className="font-medium text-zinc-900 text-xs truncate">
                          {match.name}
                        </div>
                        <div className="text-[11px] text-zinc-500 truncate">
                          {compactInternalLocation(
                            match.formatted_address ?? '',
                          )}
                        </div>
                      </button>
                    ))
                  )}

                  {/* Google results */}
                  <SectionHeader
                    label="Google results"
                    count={search.googleResults.length}
                    total={search.googleResults.length}
                    showAll={showAllGoogle}
                    onToggle={() => setShowAllGoogle((v) => !v)}
                    suffix={
                      search.googleWasBroadened
                        ? 'No nearby matches — showing broader results'
                        : undefined
                    }
                  />
                  {search.googleResults.length === 0 ? (
                    <div className="px-2 py-2 text-xs text-zinc-500">
                      {search.isSearching
                        ? 'Searching...'
                        : 'No Google matches'}
                    </div>
                  ) : (
                    googleVisible.map((p) => (
                      <button
                        key={p.place_id}
                        type="button"
                        onClick={() => handleGooglePick(p)}
                        disabled={isFetchingDetails}
                        className="w-full text-left px-2 py-1.5 hover:bg-zinc-50 border-b border-zinc-100 last:border-b-0 disabled:opacity-50"
                      >
                        <div className="flex items-center gap-1">
                          <MapPin className="h-2.5 w-2.5 text-emerald-500 flex-shrink-0" />
                          <span className="font-medium text-zinc-900 text-xs truncate">
                            {p.main_text ?? p.description}
                          </span>
                        </div>
                        {p.secondary_text && (
                          <div className="text-[11px] text-zinc-500 truncate ml-3.5">
                            {compactLocation(p.secondary_text)}
                          </div>
                        )}
                      </button>
                    ))
                  )}
                </div>

                {/* Create new manually — outside scroll area, always visible after search */}
                {search.hasSearched && !search.isSearching && (
                  <button
                    type="button"
                    onClick={handleGoToCreateNew}
                    className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 font-medium px-1"
                  >
                    <Plus className="h-3 w-3" />
                    Create new provider manually
                  </button>
                )}
              </>
            )}

            {/* Loading state when no results yet */}
            {hasSearchQuery && search.isSearching && totalResults === 0 && (
              <div className="p-4 text-center">
                <Loader2 className="mx-auto h-4 w-4 text-zinc-400 animate-spin" />
                <p className="text-xs text-zinc-500 mt-1">Searching...</p>
              </div>
            )}
          </>
        )}

        {/* ----- Step 2: Draft form ----- */}
        {step === 'draft' && (
          <div className="space-y-2">
            {/* Back button + title */}
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={handleBack}
                className="rounded p-0.5 hover:bg-zinc-100"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
              </button>
              <span className="text-xs font-semibold text-zinc-700">
                {draftStepTitle}
              </span>
            </div>

            <div className="space-y-2 p-2 border border-zinc-200 rounded-md bg-zinc-50">
              {/* Provider name */}
              <div>
                <Label className="text-xs">
                  Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={draft.name}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, name: e.target.value }))
                  }
                  placeholder="e.g. Kaiser Permanente - Orthopedics"
                  className="mt-1 h-7 text-xs"
                  autoFocus
                />
              </div>

              {/* Structured address */}
              <div>
                <Label className="text-xs">Address</Label>
                <div className="space-y-1.5 mt-1">
                  <Input
                    value={draft.addressLine1}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        addressLine1: e.target.value,
                      }))
                    }
                    placeholder="Street address"
                    className="h-7 text-xs"
                  />
                  <Input
                    value={draft.addressLine2}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        addressLine2: e.target.value,
                      }))
                    }
                    placeholder="Apt / Suite (optional)"
                    className="h-7 text-xs"
                  />
                  <div className="grid grid-cols-3 gap-1.5">
                    <Input
                      value={draft.city}
                      onChange={(e) =>
                        setDraft((d) => ({ ...d, city: e.target.value }))
                      }
                      placeholder="City"
                      className="h-7 text-xs"
                    />
                    <Input
                      value={draft.state}
                      onChange={(e) =>
                        setDraft((d) => ({ ...d, state: e.target.value }))
                      }
                      placeholder="State"
                      className="h-7 text-xs"
                    />
                    <Input
                      value={draft.postalCode}
                      onChange={(e) =>
                        setDraft((d) => ({
                          ...d,
                          postalCode: e.target.value,
                        }))
                      }
                      placeholder="ZIP"
                      className="h-7 text-xs"
                    />
                  </div>
                </div>
              </div>

              {/* Phone / Fax / Website */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Phone</Label>
                  <Input
                    value={draft.phone}
                    onChange={(e) => {
                      setDraft((d) => ({ ...d, phone: e.target.value }));
                      if (phoneError) setPhoneError(null);
                    }}
                    onBlur={handlePhoneBlur}
                    placeholder="(555) 123-4567"
                    className={`mt-1 h-7 text-xs ${phoneError ? 'border-red-500' : ''}`}
                  />
                  {phoneError && (
                    <p className="text-[10px] text-red-600 mt-0.5">{phoneError}</p>
                  )}
                </div>
                <div>
                  <Label className="text-xs">Fax</Label>
                  <Input
                    value={draft.fax}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, fax: e.target.value }))
                    }
                    placeholder="(555) 123-4567"
                    className="mt-1 h-7 text-xs"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Website</Label>
                <Input
                  value={draft.website}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, website: e.target.value }))
                  }
                  placeholder="https://..."
                  className="mt-1 h-7 text-xs"
                />
              </div>

              {/* Treatment status & type */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">
                    Treatment Status <span className="text-red-500">*</span>
                  </Label>
                  <select
                    value={treatmentStatus ?? ''}
                    onChange={(e) =>
                      setTreatmentStatus(
                        (e.target.value as ProviderTreatmentStatus) || null,
                      )
                    }
                    className="mt-1 h-7 w-full rounded-md border border-zinc-200 bg-white px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="" disabled>
                      Select status
                    </option>
                    {Object.values(ProviderTreatmentStatus)
                      .filter((s) => s !== ProviderTreatmentStatus.NEVER_TREATED)
                      .map((status) => (
                        <option key={status} value={status}>
                          {toTitleCase(status.replace(/_/g, ' '))}
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">
                    Treatment Type <span className="text-red-500">*</span>
                  </Label>
                  <select
                    value={careLevel ?? ''}
                    onChange={(e) =>
                      setCareLevel(
                        (e.target.value as TreatmentCareLevel) || null,
                      )
                    }
                    className="mt-1 h-7 w-full rounded-md border border-zinc-200 bg-white px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="" disabled>
                      Select type
                    </option>
                    {Object.values(TreatmentCareLevel).map((level) => (
                      <option key={level} value={level}>
                        {toTitleCase(level.replace(/_/g, ' '))}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Provider type selector */}
              <div className="pt-2 border-t border-zinc-200">
                <Label className="text-xs">
                  Provider type <span className="text-red-500">*</span>
                </Label>
                <div className="mt-1">
                  <ProviderTypeSelector
                    value={providerType}
                    onChange={setProviderType}
                  />
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex gap-2 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="flex-1 h-7 text-xs"
                  onClick={handleBack}
                  disabled={isCreatingProvider}
                >
                  Back
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className="flex-1 h-7 text-xs"
                  onClick={handleCreateOrReuse}
                  disabled={
                    !draft.name.trim() ||
                    !providerType ||
                    !treatmentStatus ||
                    !careLevel ||
                    !!phoneError ||
                    isCreatingProvider
                  }
                >
                  {isCreatingProvider ? (
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                  ) : (
                    'Add Provider'
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
        {/* ----- Step 3: Conflict resolution ----- */}
        {step === 'conflict' && (
          <div className="space-y-2">
            {/* Back button + title */}
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={handleBack}
                className="rounded p-0.5 hover:bg-zinc-100"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
              </button>
              <span className="text-xs font-semibold text-zinc-700">
                Possible Duplicates
              </span>
            </div>

            {/* Warning banner */}
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-medium text-amber-800">
                  {conflictCandidates.length} existing provider{conflictCandidates.length > 1 ? 's' : ''} may match.
                </p>
                <p className="text-[11px] text-amber-700 mt-0.5">
                  Select one to link, or create a new one with a reason.
                </p>
              </div>
            </div>

            {/* Candidate list */}
            {!showCreateAnywayForm && (
              <div className="space-y-1.5">
                {conflictCandidates.map((candidate) => (
                  <button
                    key={candidate.provider_service_id}
                    type="button"
                    onClick={() => setSelectedCandidateId(candidate.provider_service_id)}
                    className={`w-full text-left rounded-md border p-2 transition-colors text-xs ${
                      selectedCandidateId === candidate.provider_service_id
                        ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                        : 'border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-1">
                      <div className="min-w-0">
                        <div className="font-medium text-zinc-900 truncate">
                          {candidate.name}
                        </div>
                        {candidate.address_summary && (
                          <div className="flex items-center gap-0.5 text-[11px] text-zinc-500 mt-0.5">
                            <MapPin className="h-2.5 w-2.5 shrink-0" />
                            <span className="truncate">{candidate.address_summary}</span>
                          </div>
                        )}
                      </div>
                      <span className="text-[9px] font-medium text-zinc-400 uppercase tracking-wider whitespace-nowrap">
                        {candidate.match_source.replace(/_/g, ' ')}
                      </span>
                    </div>
                    {candidate.similarity_score != null && candidate.similarity_score < 1 && (
                      <div className="text-[10px] text-zinc-400 mt-0.5">
                        {Math.round(candidate.similarity_score * 100)}% name match
                      </div>
                    )}
                  </button>
                ))}

                {/* Use Selected button */}
                <Button
                  type="button"
                  size="sm"
                  className="w-full h-7 text-xs"
                  onClick={handleConflictUseExisting}
                  disabled={
                    !selectedCandidateId ||
                    isCreatingProvider
                  }
                >
                  {isCreatingProvider ? (
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                  ) : (
                    'Use Selected Provider'
                  )}
                </Button>

                {/* Create anyway link */}
                <button
                  type="button"
                  onClick={() => setShowCreateAnywayForm(true)}
                  className="w-full text-left rounded-md border border-dashed border-zinc-300 p-2 text-xs text-zinc-500 hover:border-zinc-400 hover:text-zinc-700 transition-colors"
                >
                  None of these — create a new provider anyway
                </button>
              </div>
            )}

            {/* Create Anyway form */}
            {showCreateAnywayForm && (
              <div className="space-y-2">
                <div>
                  <Label className="text-xs">Why are you creating a new provider?</Label>
                  <Textarea
                    value={createAnywayReason}
                    onChange={(e) => setCreateAnywayReason(e.target.value)}
                    placeholder="e.g. Different department, different location, etc."
                    rows={2}
                    className="mt-1 text-xs min-h-[48px]"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="flex-1 h-7 text-xs"
                    onClick={() => {
                      setShowCreateAnywayForm(false);
                      setSelectedCandidateId(conflictCandidates[0]?.provider_service_id ?? null);
                    }}
                    disabled={isCreatingProvider}
                  >
                    Back to list
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    className="flex-1 h-7 text-xs"
                    onClick={handleConflictCreateAnyway}
                    disabled={
                      !createAnywayReason.trim() ||
                      isCreatingProvider
                    }
                  >
                    {isCreatingProvider ? (
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                    ) : (
                      'Create Anyway'
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Section header sub-component
// ---------------------------------------------------------------------------

const SectionHeader = ({
  label,
  count,
  total,
  showAll,
  onToggle,
  suffix,
}: {
  label: string;
  count: number;
  total: number;
  showAll: boolean;
  onToggle: () => void;
  suffix?: string;
}) => (
  <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-zinc-200 px-2 py-1.5">
    <div className="flex items-center justify-between">
      <div className="text-xs font-semibold text-zinc-700">
        {label}
        {count > 0 && (
          <span className="ml-1 text-zinc-500 font-normal">({count})</span>
        )}
        {suffix && (
          <span className="ml-2 text-[11px] text-zinc-500 font-normal">
            {suffix}
          </span>
        )}
      </div>
      {total > 6 && (
        <button
          type="button"
          className="text-xs text-zinc-500 hover:text-zinc-700 underline"
          onClick={onToggle}
        >
          {showAll ? 'Show less' : `Show all (${total})`}
        </button>
      )}
    </div>
  </div>
);
